<template>
  <main class="sf-page">
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Sur le terrain</p>
        <h1 class="sf-h1">Nos <em>Projets</em></h1>
        <p class="sf-page-hero__sub">Découvrez nos initiatives pour un impact social durable et une transformation numérique inclusive.</p>
      </div>
    </div>

    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
    <template v-else>
      <!-- Featured -->
      <section v-if="featuredProject" class="sf-section sf-section--cream" style="padding-bottom:0">
        <div class="sf-container">
          <span class="sf-label">Projet Phare</span>
          <div class="pj-featured" data-aos="fade-up">
            <div class="pj-featured__media">
              <img v-if="featuredProject.featured_image_url" :src="featuredProject.featured_image_url" :alt="featuredProject.title" />
              <div v-else class="pj-placeholder"><component :is="getIconComponent(featuredProject.icon)" /></div>
              <span class="sf-card__cat">{{ featuredProject.category }}</span>
            </div>
            <div class="pj-featured__body">
              <h2 class="sf-h2" style="margin-bottom:.75rem">{{ featuredProject.title }}</h2>
              <p class="pj-featured__desc">{{ featuredProject.description }}</p>
              <div class="pj-featured__meta">
                <span class="sf-chip"><Users />{{ featuredProject.formatted_beneficiaries||featuredProject.beneficiaries+'+' }} bénéficiaires</span>
                <span class="sf-chip"><MapPin />{{ featuredProject.location }}</span>
              </div>
              <div class="sf-progress pj-featured__progress">
                <div class="sf-progress__track"><div class="sf-progress__fill" :style="{'--target':featuredProject.progress+'%'}"></div></div>
                <span class="sf-progress__pct">{{ featuredProject.progress }}%</span>
              </div>
              <router-link :to="`/projet/${featuredProject.slug||featuredProject.id}`">
                <button class="sf-btn sf-btn--terra" style="margin-top:1.5rem">Découvrir <ArrowRight class="sf-btn__icon" /></button>
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <!-- Filters -->
      <section class="sf-section sf-section--cream-d" style="padding-top:2rem;padding-bottom:1rem">
        <div class="sf-container">
          <div class="pj-filter-row">
            <div class="sf-filters" style="margin-bottom:0">
              <button class="sf-pill" :class="{'sf-pill--active':selectedCategory===''}" @click="filterByCategory('')">Tous</button>
              <button v-for="cat in categories" :key="cat" class="sf-pill" :class="{'sf-pill--active':selectedCategory===cat}" @click="filterByCategory(cat)">{{ cat }}</button>
            </div>
            <div class="sf-search-wrap"><Search /><input v-model="searchQuery" @input="debouncedSearch" placeholder="Rechercher…" class="sf-input" style="max-width:220px" /></div>
          </div>
        </div>
      </section>

      <!-- Grid -->
      <section class="sf-section sf-section--cream-d">
        <div class="sf-container">
          <div v-if="!regularProjects.length" class="sf-empty"><FolderOpen class="sf-empty__icon" /><p class="sf-empty__title">Aucun projet trouvé</p></div>
          <div v-else class="pj-grid">
            <div v-for="(pj, i) in regularProjects" :key="pj.id" class="sf-card" data-aos="fade-up" :data-aos-delay="i*70">
              <div class="sf-card__media" style="height:190px">
                <img v-if="pj.featured_image" :src="imageUrl+pj.featured_image" :alt="pj.title" loading="lazy" />
                <div v-else class="pj-placeholder"><component :is="getIconComponent(pj.icon)" /></div>
                <span class="sf-card__cat">{{ pj.category }}</span>
              </div>
              <div class="sf-card__body">
                <div class="pj-card-head">
                  <div class="pj-card-icon"><component :is="getIconComponent(pj.icon)" /></div>
                  <h3 class="sf-h3" style="font-size:1.15rem">{{ pj.title }}</h3>
                </div>
                <p class="pj-card-desc">{{ pj.description }}</p>
                <span class="sf-chip" style="margin-bottom:.85rem"><MapPin />{{ pj.location }}</span>
                <div class="sf-progress">
                  <div class="sf-progress__track"><div class="sf-progress__fill" :style="{'--target':pj.progress+'%'}"></div></div>
                  <span class="sf-progress__pct">{{ pj.progress }}%</span>
                </div>
              </div>
              <div style="padding:0 1.5rem 1.5rem">
                <router-link :to="`/projects/${pj.slug||pj.id}`">
                  <button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">En savoir plus <ArrowRight class="sf-btn__icon" /></button>
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="pagination.last_page>1" class="sf-pagination">
            <button class="sf-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="sf-page-btn" :class="{'sf-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="sf-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <div class="sf-cta-banner" data-aos="fade-up">
            <p class="sf-cta-banner__title">Un projet à <em>proposer</em> ?</p>
            <p class="sf-cta-banner__sub">Nous sommes toujours à la recherche de nouvelles initiatives à soutenir. Contactez-nous pour discuter de votre idée.</p>
            <router-link to="/contact"><button class="sf-btn sf-btn--amber">Proposer un projet</button></router-link>
          </div>
        </div>
      </section>
    </template>
    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { ArrowRight, Wifi, Users, Leaf, BookOpen, FolderOpen, MapPin, Search, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-vue-next'
export default {
  name:'ProjectsPage',
  components:{ ArrowRight, Wifi, Users, Leaf, BookOpen, FolderOpen, MapPin, Search, ChevronLeft, ChevronRight, CheckCircle },
  data(){return{searchQuery:'',selectedCategory:'',displayedPages:[],toastMessage:''}},
  computed:{
    ...mapState({projects:s=>s.projects,featuredProject:s=>s.featuredProject,loading:s=>s.loading,pagination:s=>s.projectsPagination,imageUrl:s=>s.imageUrl}),
    regularProjects(){return this.projects.filter(p=>!p.is_featured)},
    categories(){return[...new Set(this.projects.map(p=>p.category))].filter(Boolean).slice(0,5)}
  },
  watch:{'pagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}},
  created(){this.debouncedSearch=debounce(this.handleSearch,500);this.loadProjects()},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchPublicProjects','fetchFeaturedProject','setProjectFilter','setProjectPage']),
    async loadProjects(){try{await Promise.all([this.fetchPublicProjects(),this.fetchFeaturedProject()])}catch(e){this.toastMessage='Erreur de chargement'}},
    handleSearch(){this.setProjectFilter({key:'search',value:this.searchQuery});this.setProjectPage(1);this.fetchPublicProjects()},
    filterByCategory(c){this.selectedCategory=c;this.setProjectFilter({key:'category',value:c});this.setProjectPage(1);this.fetchPublicProjects()},
    changePage(p){if(p>=1&&p<=this.pagination.last_page){this.setProjectPage(p);this.fetchPublicProjects();window.scrollTo({top:0,behavior:'smooth'})}},
    updateDisplayedPages(){const c=this.pagination.current_page,l=this.pagination.last_page,d=2;let pg=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pg.push(i)}this.displayedPages=pg},
    getIconComponent(n){return{Wifi,Users,Leaf,BookOpen}[n]||FolderOpen}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.pj-featured{display:grid;grid-template-columns:1fr 1fr;gap:0;border-radius:8px;overflow:hidden;box-shadow:var(--shadow-md);background:var(--white);margin-top:1rem}
@media(max-width:768px){.pj-featured{grid-template-columns:1fr}}
.pj-featured__media{min-height:300px;overflow:hidden;background:var(--cream-d);position:relative}
.pj-featured__media img{width:100%;height:100%;object-fit:cover}
.pj-featured__body{padding:2.5rem 2rem;display:flex;flex-direction:column}
.pj-featured__desc{font-size:.92rem;color:var(--ink-60);line-height:1.7;word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;margin-bottom:1rem;flex:1}
.pj-featured__meta{display:flex;flex-wrap:wrap;gap:1rem}
.pj-featured__progress{margin-top:1.25rem}
.pj-placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.pj-placeholder :deep(svg){width:48px;height:48px}
.pj-filter-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem}
.pj-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:1.5rem}
.pj-card-head{display:flex;align-items:center;gap:.75rem;margin-bottom:.65rem}
.pj-card-icon{width:38px;height:38px;border-radius:6px;background:rgba(27,58,45,.1);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.pj-card-icon :deep(svg){width:18px;height:18px;color:var(--terra)}
.pj-card-desc{font-size:.83rem;color:var(--ink-60);line-height:1.55;margin-bottom:.65rem;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
</style>
